/* Created by Interactive 63 - https://interactive63.com */
/**
 * The Inspector panel. It shows scene settings when nothing is selected, and otherwise the
 * selected elements' properties grouped by kind, with a keyframe diamond per property, plus the
 * selected keyframes' time, value and easing. Structure is rebuilt only when the selection or
 * the set of animated properties changes; values refresh in place.
 */
import type { ElementId } from '../../../core/model/types';
import {
  propertiesForTag,
  type PropertyDefinition,
  type PropertyGroup,
} from '../../../core/properties/registry';
import type { PlaybackController } from '../../playback';
import { setAutoKey } from '../../store/actions';
import { readStorage, writeStorage } from '../../storage';
import { Component } from '../../ui/component';
import { h } from '../../ui/dom';
import type { StagePanel } from '../stage/stage';
import { ElementSection } from './element-section';
import { StopSection } from './stop-section';
import { TextSection } from './text-section';
import { TEXT_TAGS } from '../../../core/text/text';
import { onTextEditingRequest } from '../../text-editing';
import type { PresetsOpener } from '../../dialogs/lazy-dialogs';
import { createInspectorContext, type InspectorContext } from './inspector-context';
import { KeyframeSection } from './keyframe-section';
import { PropertyRow } from './property-row';
import { ArtboardSection } from './artboard-section';
import { SceneSection } from './scene-section';

export interface InspectorProps {
  playback: PlaybackController;
  stage: StagePanel;
  presets: PresetsOpener;
}

const COLLAPSED_KEY = 'svg-tempo:inspector-collapsed';
const REFRESH_INTERVAL_MS = 1000 / 15;

const GROUPS: { id: PropertyGroup; label: string }[] = [
  { id: 'transform', label: 'Transform' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'text', label: 'Text' },
  { id: 'stroke', label: 'Stroke' },
  { id: 'effects', label: 'Effects' },
  { id: 'shape', label: 'Shape' },
];

/** Loads the collapsed group names saved from a previous session. */
function loadCollapsed(): Set<string> {
  try {
    const parsed: unknown = JSON.parse(readStorage(COLLAPSED_KEY) ?? '[]');
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

export class Inspector extends Component<InspectorProps> {
  private body!: HTMLElement;
  private ctx!: InspectorContext;
  private autoKey!: HTMLButtonElement;
  private rows: PropertyRow[] = [];
  private sections: { refresh(): void }[] = [];
  private textSection: TextSection | null = null;
  private collapsed = loadCollapsed();
  private lastRefresh = 0;

  /** Creates the panel section. */
  protected override createRoot(): HTMLElement {
    return h('section', { class: ['panel', 'panel-inspector'], 'aria-label': 'Inspector' });
  }

  /** Builds the header and subscribes to the store and playback. */
  protected override init(): void {
    this.ctx = createInspectorContext(this.store, this.props.playback, this.props.stage);
    this.autoKey = h('button', {
      type: 'button',
      class: 'autokey',
      'aria-label': 'Auto-key',
      title: 'Auto-key: edits to animated properties create keyframes at the playhead',
    });
    this.autoKey.addEventListener('click', () => {
      setAutoKey(this.store, !this.store.getState().editor.autoKey);
    });
    this.body = h('div', { class: 'inspector-body' });
    const presets = h(
      'button',
      { type: 'button', class: 'presets-button', title: 'Animation presets' },
      'Presets',
    );
    presets.addEventListener('click', () => {
      this.props.presets.open(presets);
    });
    this.el.append(
      h('div', { class: 'panel-heading' }, h('h2', null, 'Inspector'), presets, this.autoKey),
      this.body,
    );

    this.select(
      (s) => this.structureKey(s.project.elements, s.project.tracks, s.editor.selection),
      () => {
        this.rebuild();
      },
      { immediate: true },
    );
    this.select(
      (s) => s.project,
      () => {
        this.refresh();
      },
    );
    this.select(
      (s) => [s.editor.playhead, s.editor.isPlaying] as const,
      () => {
        this.refresh();
      },
      { equality: 'shallow' },
    );
    this.select(
      (s) => s.editor.autoKey,
      (on) => {
        this.autoKey.setAttribute('aria-pressed', String(on));
      },
      { immediate: true },
    );
    this.addCleanup(
      onTextEditingRequest(() => {
        this.textSection?.focusContent();
      }),
    );
    this.addCleanup(
      this.props.playback.onTick(() => {
        const now = performance.now();
        if (now - this.lastRefresh < REFRESH_INTERVAL_MS) return;
        this.lastRefresh = now;
        this.refresh();
      }),
    );
  }

  /** A string that changes only when the layout of the panel must change. */
  private structureKey(
    elements: Record<string, { tag: string }>,
    tracks: readonly { elementId: string; property: string }[],
    selection: { elementIds: readonly string[]; keyframeIds: readonly string[] },
  ): string {
    const perElement = selection.elementIds.map((id) => [
      id,
      elements[id]?.tag,
      tracks
        .filter((t) => t.elementId === id)
        .map((t) => t.property)
        .sort(),
    ]);
    return JSON.stringify([perElement, selection.keyframeIds]);
  }

  /** Refreshes values in every section and row without rebuilding them. */
  private refresh(): void {
    for (const section of this.sections) section.refresh();
    for (const row of this.rows) row.refresh();
  }

  /** Rebuilds the panel for the current selection. */
  private rebuild(): void {
    const { project, editor } = this.store.getState();
    this.rows = [];
    this.sections = [];
    this.textSection = null;
    const content: HTMLElement[] = [];

    const ids = editor.selection.elementIds.filter((id) => id in project.elements);
    if (ids.length === 0) {
      const scene = new SceneSection(this.store);
      const artboard = new ArtboardSection(this.store, {
        artworkBounds: () => this.props.stage.artworkBounds(),
      });
      this.sections.push(scene, artboard);
      content.push(scene.el, artboard.el);
    } else {
      if (ids.length === 1 && ids[0] !== undefined) {
        const tag = project.elements[ids[0]]?.tag ?? '';
        const element = new ElementSection(
          this.store,
          ids[0],
          propertiesForTag(tag).some((p) => p.usesTransform),
          () => this.props.stage.localSize(ids[0] ?? ''),
        );
        this.sections.push(element);
        content.push(element.el);
        if (tag === 'stop') {
          const stop = new StopSection(this.store, ids[0]);
          this.sections.push(stop);
          content.push(stop.el);
        }
        if (TEXT_TAGS.has(tag)) {
          this.textSection = new TextSection(this.store, ids[0]);
          this.sections.push(this.textSection);
          content.push(this.textSection.el);
        }
      } else {
        content.push(
          h(
            'div',
            { class: 'inspector-section selection-count' },
            `${ids.length} elements selected`,
          ),
        );
      }
      content.push(...this.buildPropertySections(ids));
    }

    if (editor.selection.keyframeIds.length > 0) {
      const section = new KeyframeSection(this.ctx, editor.selection.keyframeIds);
      this.sections.push(section);
      content.push(section.el);
    }

    this.body.replaceChildren(...content);
    this.refresh();
  }

  /** Builds the collapsible property groups for the selected elements. */
  private buildPropertySections(ids: readonly ElementId[]): HTMLElement[] {
    const { project } = this.store.getState();
    const tags = ids.map((id) => project.elements[id]?.tag ?? '');
    const available = propertiesForTag(tags[0] ?? '').filter((definition) =>
      tags.every((tag) => definition.appliesTo(tag.toLowerCase())),
    );

    const sections: HTMLElement[] = [];
    for (const group of GROUPS) {
      const inGroup = available.filter((p) => p.group === group.id);
      if (inGroup.length === 0) continue;
      const open = !this.collapsed.has(group.id);

      const heading = h(
        'button',
        { type: 'button', class: 'group-heading', 'aria-expanded': String(open) },
        h('span', { class: 'group-chevron' }, open ? '▾' : '▸'),
        group.label,
      );
      heading.addEventListener('click', () => {
        if (this.collapsed.has(group.id)) this.collapsed.delete(group.id);
        else this.collapsed.add(group.id);
        writeStorage(COLLAPSED_KEY, JSON.stringify([...this.collapsed]));
        this.rebuild();
      });

      const section = h(
        'section',
        { class: 'inspector-section property-group', 'data-group': group.id },
        heading,
      );
      if (open) {
        for (const definition of inGroup) section.append(this.buildRow(definition, ids));
      }
      sections.push(section);
    }
    return sections;
  }

  /** Creates and registers a property row. */
  private buildRow(definition: PropertyDefinition, ids: readonly ElementId[]): HTMLElement {
    const row = new PropertyRow(this.ctx, definition, ids);
    this.rows.push(row);
    return row.el;
  }
}
